import { useCallback, useEffect, useRef, useState } from "react";

import {
  areRecentSearchWritesBlocked,
  clearRecentSearches,
  recentSearchesKey,
} from "@/utils/recentSearchesStorage";
import { onSessionEnd } from "@/utils/sessionTeardown";

export type RecentSearch = {
  query: string;
  docCount: number;
};

const MAX_ENTRIES = 10;

const load = (caseNumber: string): RecentSearch[] => {
  try {
    const raw = localStorage.getItem(recentSearchesKey(caseNumber));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

// Per-case localStorage-backed history of semantic searches. `docCount` is the
// number of results the search returned, surfaced as the "N docs" count in the
// dropdown. There is no backend persistence (frontend-only by design). Logout
// clears the stored history (MDRS-558).
export const useRecentSearches = (caseNumber: string) => {
  const [items, setItems] = useState<RecentSearch[]>(() => load(caseNumber));

  // Re-sync when the hook stays mounted across a case change.
  useEffect(() => {
    setItems(load(caseNumber));
  }, [caseNumber]);

  // Logout blocks writes first, then awaits the recovery clear (up to 1s)
  // before this runs. Clear again and drop the in-memory copy; the ref stops
  // recording for the rest of this mount.
  const sessionEndedRef = useRef(false);
  useEffect(
    () =>
      onSessionEnd(() => {
        sessionEndedRef.current = true;
        clearRecentSearches();
        setItems([]);
      }),
    []
  );

  const add = useCallback(
    (query: string, docCount: number) => {
      const trimmed = query.trim();
      if (!trimmed || sessionEndedRef.current || areRecentSearchWritesBlocked()) {
        return;
      }

      setItems((prev) => {
        // The update can still be queued when logout's teardown runs.
        if (sessionEndedRef.current || areRecentSearchWritesBlocked()) {
          return prev;
        }
        const next = [
          { query: trimmed, docCount },
          ...prev.filter((item) => item.query !== trimmed),
        ].slice(0, MAX_ENTRIES);

        try {
          localStorage.setItem(recentSearchesKey(caseNumber), JSON.stringify(next));
        } catch {
          // Ignore quota / unavailable storage — history is best-effort.
        }
        return next;
      });
    },
    [caseNumber]
  );

  return { items, add };
};
